import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import { apiCall } from 'utils/swaggerClient';

import { createStructuredSelector } from 'reselect';
import { RadioGroup } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';
import styled from 'styled-components';
import moment from 'moment';

import { primaryDarkBlue } from 'theme/_variables.scss';
import MessageDialog from 'components/Dialogs/MessageDialog/Loadable';
import InvoiceDialog from 'components/Dialogs/InvoiceDialog/Loadable';
import CurrentBalanceDialog from 'components/Dialogs/CurrentBalanceDialog/Loadable';
import {
  Button,
  Drawer,
  Radio,
  Input,
  Div,
  Span,
  H2,
  F,
  P,
} from 'components/Ui';
import {
  accountIcons,
  BlackBtn,
  lockIcon,
  InfoIcon,
  warningRed,
  arrowBackward,
} from 'images';
import { formatCurrency } from 'utils/common';
import { dataLayerPush } from 'utils/tracking';
import EditBankAccountDrawer from 'containers/PrimoProfile/BillingInformation/BankAccountForm/EditBankAccountDrawer';
import EditCreditCardDrawer from 'containers/PrimoProfile/BillingInformation/CreditCardForm/EditCreditCardDrawer';
import {
  selectPaymentMethods,
  selectCurrentPaymentMethod,
  selectBillingSummary,
  selectBankAccounts,
  selectIsHardLockout,
  selectCreditCardsFetchError,
  selectBankAccountsFetchError,
  selectBillingSummaryFetchError,
} from 'containers/PrimoProfile/selectors';
import {
  fetchPaymentMethods,
  setCurrentPaymentMethod,
  resetAddCardSucessful,
  resetPayMyBillError,
} from 'containers/PrimoProfile/actions';
import CardSurcharges from 'containers/PrimoProfile/Payment/Surcharges/CardSurcharges';
import {
  EDIT_CREDIT_CARD,
  EDIT_BANK_ACCOUNT,
} from 'containers/PrimoProfile/BillingInformation/constants';

import {
  selectPaymentSubmitted,
  selectPaymentFetching,
  selectPaymentError,
  selectSelectedServiceLocationId,
  selectBillingAccountId,
} from 'containers/PrimoAccount/selectors';
import { makePayment, resetMakePayment } from 'containers/PrimoAccount/actions';

import { selectIsMobileAppView } from 'containers/Authentication/selectors';
import classnames from 'classnames';
import * as drawerActions from 'containers/DrawerManager/actions';
import { isCardExpired } from 'containers/PrimoProfile/Payment/utils';
import styles from './styles.scss';

const PaymentInfoTitle = styled.div`
  font-size: 18px;
  color: ${primaryDarkBlue};
  margin-left: 5px;
`;

const PaymentMethodTitle = styled.div`
  font-size: 18px;
  margin-left: 5px;
  font-weight: normal;
  min-width: 50px;
`;

const WarningIcon = styled.img`
  margin-bottom: 2px;
`;

const AMT_TYPES = {
  currentBalance: 'currentBalance',
  dueAmount: 'dueAmount',
  customAmount: 'customAmount',
};

const BILLING_SUMMARY_AMT_KEYS = {
  [AMT_TYPES.currentBalance]: 'totalCurrentBalance',
  [AMT_TYPES.dueAmount]: 'amountDue',
};

const PayMethodRadio = ({ disabled = false, method }) => {
  const isExpired = isCardExpired(method);
  return (
    <Radio
      disabled={disabled}
      value={method.id}
      label={
        !method.cardType ? (
          <div className={styles.paymentLabel}>
            <Div className={styles.accountType}>{method.accountType}</Div>
            &nbsp;<F>ending in</F>
            <PaymentMethodTitle>{method.last4}</PaymentMethodTitle>
          </div>
        ) : (
          <div>
            <div className={styles.paymentLabel}>
              <Span>Credit/debit card ending in</Span>
              <PaymentMethodTitle>{method.last4}</PaymentMethodTitle>
            </div>
            <div>
              <div
                className={classnames(
                  { [styles.red]: isExpired },
                  styles.expireText,
                )}
              >
                <Span>Expires</Span> {method.expirationMonth}
                /20
                {method.expirationYear}
              </div>
            </div>
          </div>
        )
      }
    />
  );
};

PayMethodRadio.propTypes = {
  disabled: PropTypes.bool,
  method: PropTypes.object,
};

class PayBillDrawer extends Component {
  constructor(props) {
    super(props);

    const currentBalance = get(
      this.props,
      'billingSummary.totalCurrentBalance',
      null,
    );

    this.state = {
      amountType: AMT_TYPES.dueAmount,
      currentBalance,
      customAmountInput: '',
      customAmountFloat: 0,
      paymentId: null,
      paymentMethod: null,
      showPaymentAmountErrorDialog: false,
      showChoosePaymentMethodErrorDialog: false,
      openCurrentBalanceDialog: false,
      showInvoiceErrorDialog: false,
      invoiceErrorMessage: null,
      openInvoiceDialog: false,
      invoiceFile: null,
    };
  }

  componentDidMount() {
    /* eslint-disable no-shadow */
    const { fetchPaymentMethods, resetPayMyBillError } = this.props;
    const { currentBalance } = this.state;
    resetPayMyBillError();
    fetchPaymentMethods();
    this.initializeFields();
    ValidatorForm.addValidationRule('isAmountLessThenBalance', (value) => {
      const valueNum = parseFloat(value, 10);
      if (
        value !== '' &&
        // eslint-disable-next-line no-restricted-globals
        (isNaN(valueNum) ||
          valueNum < 1 ||
          valueNum > parseFloat(currentBalance, 10))
      ) {
        return false;
      }
      return true;
    });
  }

  getPayAmount = () => {
    const { amountType, customAmountFloat } = this.state;
    const { billingSummary } = this.props;
    if (amountType === AMT_TYPES.customAmount) {
      return customAmountFloat;
    }
    return billingSummary[BILLING_SUMMARY_AMT_KEYS[amountType]];
  };

  getNonExpiredPaymentMethod = () => {
    const {
      currentPaymentMethod,
      paymentMethods,
      setCurrentPaymentMethod,
    } = this.props;

    if (!isCardExpired(currentPaymentMethod)) {
      return currentPaymentMethod;
    }

    const nonExpiredMethod = paymentMethods.find(
      (mthd) => !isCardExpired(mthd),
    );

    if (!nonExpiredMethod) return currentPaymentMethod;

    setCurrentPaymentMethod(nonExpiredMethod.id);

    return nonExpiredMethod;
  };

  initializeFields = () => {
    const {
      isHardLockout,
      billingSummary: { amountDue, totalCurrentBalance },
    } = this.props;

    const paymentMethod = this.getNonExpiredPaymentMethod();
    const paymentId = paymentMethod.id;

    // eslint-disable-next-line no-nested-ternary
    let amountType =
      amountDue > 0
        ? AMT_TYPES.dueAmount
        : totalCurrentBalance > 0
        ? AMT_TYPES.currentBalance
        : AMT_TYPES.customAmount;
    if (isHardLockout) amountType = AMT_TYPES.currentBalance;

    this.setState({
      paymentId,
      paymentMethod,
      amountType,
    });
  };

  componentDidUpdate(prevProps) {
    const hasNewBalance =
      prevProps.billingSummary.totalCurrentBalance !==
      this.props.billingSummary.totalCurrentBalance;
    const hasNewPayMethod =
      prevProps.currentPaymentMethod?.id !==
      this.props.currentPaymentMethod?.id;

    if (hasNewBalance || hasNewPayMethod) {
      this.initializeFields();
    }
  }

  componentWillUnmount() {
    ValidatorForm.removeValidationRule('isAmountLessThenBalance');
  }

  showBalanceDialog = () => {
    this.setState({ openCurrentBalanceDialog: true });
  };

  hideBalanceDialog = () => {
    this.setState({ openCurrentBalanceDialog: false });
  };

  showInvoiceDialog = async () => {
    let statementDate = 'Error: Something went wrong retrieving dates.';
    try {
      const response = await apiCall({
        operationId: 'BillingAccounts.getBillingAccountStatements',
        parameters: {
          billingAccountId: this.props.selectedBillingAccountId,
          fromDate: '2019-01-01',
          toDate: moment().format('YYYY-MM-DD'),
        },
      });

      if (isEmpty(response)) {
        statementDate = 'Error: No invoices were found.';
        throw Error();
      }
      const { billingStatements } = response;
      statementDate = billingStatements.reduce(
        (result, billingStatement) =>
          moment(result).isAfter(moment(billingStatement.statementDate))
            ? result
            : billingStatement.statementDate,
        billingStatements[0].statementDate,
      );

      const { data } = await apiCall({
        operationId: 'ServiceLocations.getServiceLocationStatementsInvoice',
        parameters: {
          serviceLocationId: this.props.serviceLocationId,
          statementDate,
        },
        fullResponse: true,
      });
      this.setState({ openInvoiceDialog: true, invoiceFile: data });
    } catch (error) {
      let errorMessage = statementDate;
      if (!statementDate.startsWith('Error')) {
        const message = get(error, 'response.body.message', null);
        // TODO: translation below
        errorMessage = message
          ? `${message} for date: ${statementDate}.`
          : `Error retrieving invoice for date: ${statementDate}.`;
      }
      const invoiceErrorMessage = (
        <>
          <Span>The invoice could not be displayed.</Span>
          <div className={styles.paymentError}>
            <WarningIcon src={warningRed} alt="Error" /> {errorMessage}
          </div>
        </>
      );
      this.setState({ showInvoiceErrorDialog: true, invoiceErrorMessage });
    }
  };

  hideInvoiceDialog = () => {
    this.setState({ openInvoiceDialog: false });
  };

  handleAmountTypeChange = (e) => {
    this.setState({ amountType: e.target.value });
  };

  handlePaymentChange = (e) => {
    const payMethodId = e.target.value;
    const { setCurrentPaymentMethod } = this.props;

    setCurrentPaymentMethod(payMethodId);
  };

  handleSubmit = () => {
    const { amountType, customAmountFloat } = this.state;
    const {
      makePayment,
      billingSummary,
      bankAccounts,
      isHardLockout,
      currentPaymentMethod,
    } = this.props;

    if (!currentPaymentMethod) {
      this.setState({ showChoosePaymentMethodErrorDialog: true });
      return;
    }

    const paymentType = bankAccounts.find(
      (pay) => pay.id === currentPaymentMethod.id,
    );
    const paymentMethodType = paymentType ? 'ACH' : 'CC';

    if (amountType === AMT_TYPES.currentBalance) {
      makePayment(
        billingSummary.totalCurrentBalance,
        currentPaymentMethod.id,
        paymentMethodType,
      );
    } else if (amountType === AMT_TYPES.dueAmount) {
      makePayment(
        billingSummary.amountDue,
        currentPaymentMethod.id,
        paymentMethodType,
      );
    } else {
      const currentBalanceNum = parseFloat(billingSummary.totalCurrentBalance);
      if (customAmountFloat <= 0 || customAmountFloat > currentBalanceNum) {
        this.setState({ showPaymentAmountErrorDialog: true });
        return;
      }
      makePayment(
        `${customAmountFloat}`,
        currentPaymentMethod.id,
        paymentMethodType,
      );
    }

    dataLayerPush(
      'SelfServe',
      {
        event: isHardLockout ? 'suspended-payment' : 'make-payment',
        type: this.state.amountType,
        method: paymentMethodType,
      },
      'dlS21',
    );
  };

  toFixedNumStr = (str, digits = 2) => {
    const i = str.indexOf('.');
    if (i < 0) return str;
    return str.slice(0, i + digits + 1);
  };

  parseFloatPrice = (str) => {
    const fixedDecimalPrice = this.toFixedNumStr(str);
    return parseFloat(fixedDecimalPrice);
  };

  debounceCustomAmount = debounce((text) => {
    if (text) {
      const customAmountFloat = this.parseFloatPrice(text);
      if (customAmountFloat >= 0) {
        this.setState({ customAmountFloat });
      }
    }
  }, 1000);

  handleCustomAmount = (e) => {
    const { value } = e.target;
    this.setState({ customAmountInput: value });
    this.debounceCustomAmount(value.trim());
  };

  showCreditCardForm = () => {
    this.props.resetAddCardSucessful();
    this.props.showDrawer(EDIT_CREDIT_CARD);
  };

  showBankAccountForm = () => this.props.showDrawer(EDIT_BANK_ACCOUNT);

  handleClosePaymentErrorDialog = () => {
    this.props.resetMakePayment();
  };

  handleClosePaymentSubmittedDialog = () => {
    this.props.resetMakePayment();
  };

  handleFetchErrorDialog = () => {
    this.props.onClose();
    this.props.resetMakePayment();
  };

  handleClosePaymentAmountErrorDialog = () => {
    this.setState({ showPaymentAmountErrorDialog: false });
  };

  handleCloseChoosePaymentMethodErrorDialog = () => {
    this.setState({ showChoosePaymentMethodErrorDialog: false });
  };

  handleCloseInvoiceErrorDialog = () => {
    const invoiceErrorMessage = null;
    this.setState({ showInvoiceErrorDialog: false, invoiceErrorMessage });
  };

  render() {
    const {
      open,
      onClose,
      paymentSubmitted,
      paymentFetching,
      paymentError,
      paymentMethods,
      billingSummary,
      resetMakePayment,
      isHardLockout,
      isMobileAppView,
      creditCardsFetchError,
      bankAccountsFetchError,
      billingSummaryFetchError,
    } = this.props;

    const {
      amountType,
      customAmountInput,
      customAmountFloat,
      paymentId,
      paymentMethod,
      showPaymentAmountErrorDialog,
      showChoosePaymentMethodErrorDialog,
      openCurrentBalanceDialog,
      openInvoiceDialog,
      invoiceFile,
      showInvoiceErrorDialog,
      invoiceErrorMessage,
    } = this.state;

    const { surchargeOptOut } = billingSummary;

    const disableSubmit =
      !paymentId || isEmpty(billingSummary) || this.getPayAmount() <= 0;

    const amountDue = get(billingSummary, 'amountDue', 0);
    const totalCurrentBalance = get(billingSummary, 'totalCurrentBalance', 0);

    const paymentAmountErrorMessage =
      'You cannot pay more than your current balance. Please make a payment for an equal or lower amount.';

    const choosePaymentMethodErrorMessage =
      'You must choose a valid payment method or add a new payment method.';

    const customAmountClass =
      amountType === AMT_TYPES.customAmount
        ? styles.customAmountSelected
        : styles.customAmount;

    if (
      open &&
      (creditCardsFetchError ||
        bankAccountsFetchError ||
        billingSummaryFetchError)
    ) {
      const fetchErrorMessage = (
        <div className={styles.paymentError}>
          <WarningIcon src={warningRed} alt="Error" />{' '}
          {creditCardsFetchError ||
            bankAccountsFetchError ||
            billingSummaryFetchError ||
            ''}
        </div>
      );
      return (
        <MessageDialog
          isCloseIcon={!isMobileAppView}
          open
          onClose={() => this.handleFetchErrorDialog()}
          message={fetchErrorMessage}
        />
      );
    }

    if (
      open &&
      (creditCardsFetchError ||
        bankAccountsFetchError ||
        billingSummaryFetchError)
    ) {
      const fetchErrorTitle = 'Sorry!';
      const fetchErrorMessage = (
        <div className={styles.paymentError}>
          <WarningIcon src={warningRed} alt="Error" />{' '}
          {creditCardsFetchError ||
            bankAccountsFetchError ||
            billingSummaryFetchError ||
            ''}
        </div>
      );
      return (
        <MessageDialog
          isCloseIcon={!isMobileAppView}
          open
          onClose={() => this.handleFetchErrorDialog()}
          title={fetchErrorTitle}
          message={fetchErrorMessage}
        />
      );
    }

    return (
      <Drawer
        open={open}
        headerTitle="Make a payment"
        data-tracking={
          isHardLockout ? 'Suspended account make a payment' : 'Make a payment'
        }
        hideCloseButton={isMobileAppView}
        onClose={() => {
          onClose();
          resetMakePayment();
        }}
      >
        {!paymentSubmitted ? (
          <>
            <ValidatorForm onSubmit={this.handleSubmit}>
              {!isHardLockout ? (
                <div className={styles.amount}>
                  <H2 className={classnames(styles.header, styles.generic)}>
                    Payment amount
                  </H2>
                  <RadioGroup
                    name="amountType"
                    className={classnames(
                      styles.radioGroup,
                      styles.radioGroupOverRide,
                    )}
                    value={amountType}
                    onChange={this.handleAmountTypeChange}
                  >
                    <div className={styles.currentBalance}>
                      <Radio
                        value="dueAmount"
                        disabled={amountDue === 0}
                        classes={{
                          root: styles.radioWrapper,
                        }}
                        label={
                          <div className={styles.innerRadioContent}>
                            <div className={styles.line}>
                              <Div className={styles.paymentLabel}>
                                Amount Due:
                              </Div>
                              <PaymentInfoTitle>
                                {formatCurrency(amountDue, '$0.00')}
                              </PaymentInfoTitle>
                            </div>
                            {amountType === AMT_TYPES.dueAmount && (
                              <CardSurcharges
                                method={paymentMethod}
                                amount={amountDue}
                              />
                            )}
                            <div
                              className={styles.lockedWhatis}
                              onClick={this.showInvoiceDialog}
                              onKeyPress={this.showInvoiceDialog}
                              role="button"
                              tabIndex={-1}
                            >
                              <Span>See current invoice</Span>
                            </div>
                          </div>
                        }
                      />
                    </div>
                    <div className={styles.currentBalance}>
                      <Radio
                        classes={{
                          root: styles.radioWrapper,
                        }}
                        value="currentBalance"
                        disabled={totalCurrentBalance === 0}
                        label={
                          <div className={styles.innerRadioContent}>
                            <div className={styles.line}>
                              <div>
                                <Div className={styles.paymentLabel}>
                                  Current balance:
                                </Div>
                              </div>
                              <PaymentInfoTitle>
                                {totalCurrentBalance
                                  ? formatCurrency(totalCurrentBalance)
                                  : formatCurrency(0)}
                              </PaymentInfoTitle>{' '}
                            </div>
                            {amountType === AMT_TYPES.currentBalance && (
                              <CardSurcharges
                                method={paymentMethod}
                                amount={totalCurrentBalance}
                              />
                            )}
                            <div
                              className={styles.lockedWhatis}
                              onClick={this.showBalanceDialog}
                              onKeyPress={this.showBalanceDialog}
                              role="button"
                              tabIndex={0}
                            >
                              <span className={styles.informationImage}>
                                <img src={InfoIcon} alt="" />
                              </span>
                              <Span>what is this</Span>
                            </div>
                          </div>
                        }
                      />
                    </div>
                    <div className={styles.currentBalance}>
                      <Radio
                        value="customAmount"
                        classes={{ root: styles.radioWrapper }}
                        label={
                          <div className={styles.innerRadioContent}>
                            <div className={styles.line}>
                              <div>
                                <Div className={styles.paymentLabel}>
                                  Other amount:
                                </Div>
                              </div>
                              <div className={customAmountClass}>
                                <Input
                                  containerClassName={styles['input-root']}
                                  labelClassName={styles.errorLabel}
                                  name="customAmountInput"
                                  validators={
                                    amountType === AMT_TYPES.customAmount
                                      ? [
                                          'required',
                                          'floatNumber',
                                          'isAmountLessThenBalance',
                                        ]
                                      : []
                                  }
                                  errorMessages={[
                                    'Amount is required',
                                    'Not valid amount',
                                    'Amount should be 1.00 or more, and less than balance',
                                  ]}
                                  value={customAmountInput}
                                  onChange={this.handleCustomAmount}
                                  disabled={
                                    amountType !== AMT_TYPES.customAmount
                                  }
                                />
                              </div>
                            </div>
                            {amountType === AMT_TYPES.customAmount && (
                              <CardSurcharges
                                method={paymentMethod}
                                amount={customAmountFloat}
                              />
                            )}
                          </div>
                        }
                      />
                    </div>
                  </RadioGroup>
                </div>
              ) : (
                <div>
                  <div className={styles.lockContainer}>
                    <img
                      src={lockIcon}
                      className={styles.lockImage}
                      alt="lock"
                    />
                  </div>
                  <Div className={styles.lockoutHeader}>
                    Your account is currently suspended awaiting payment
                  </Div>
                  <Div className={styles.lockoutText}>
                    Your account is past due. Please make a payment for the
                    amount of your full balance to restore access
                  </Div>
                  <div className={styles.amount}>
                    <Div className={styles.header}>Payment amount</Div>
                  </div>
                  <div className={styles.lockedPaymentAmount}>
                    <div className={styles.lockedCurrentBalance}>
                      <Div className={styles.lockedBalance}>
                        Current Balance
                      </Div>
                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                      <div
                        className={styles.lockedWhatis}
                        onClick={this.showBalanceDialog}
                        onKeyPress={this.showBalanceDialog}
                        role="button"
                        tabIndex={-1}
                      >
                        <span className={styles.informationImage}>
                          <img src={InfoIcon} alt="" />
                        </span>
                        <Span>what is this</Span>
                      </div>
                      <div className={styles.lockedAmount}>
                        {formatCurrency(totalCurrentBalance)}
                      </div>
                    </div>
                    <CardSurcharges
                      method={paymentMethod}
                      amount={totalCurrentBalance}
                    />
                  </div>
                </div>
              )}
              <div className={styles.cards}>
                <H2 className={styles.header}>Payment method</H2>
                <div>
                  <RadioGroup
                    name="paymentMethod"
                    className={styles.radioGroupNoBorder}
                    value={paymentId}
                    onChange={this.handlePaymentChange}
                  >
                    {paymentMethods.map((method) => (
                      <PayMethodRadio
                        key={method.id}
                        method={method}
                        disabled={isCardExpired(method)}
                      />
                    ))}
                  </RadioGroup>
                </div>
                <div className={styles.addPaymentBtn}>
                  <Button
                    icon={<img src={BlackBtn} alt="" className={styles.add} />}
                    onClick={this.showCreditCardForm}
                    link
                    type="button"
                    disabled={isEmpty(billingSummary)}
                    ariaLabel="Add a new credit/debit card"
                    data-tracking="Add Card"
                  />
                  <Span>Add a new credit/debit card</Span>
                </div>
                <div className={styles.addPaymentBtn}>
                  <Button
                    icon={<img src={BlackBtn} alt="" className={styles.add} />}
                    onClick={this.showBankAccountForm}
                    link
                    type="button"
                    disabled={isEmpty(billingSummary)}
                    ariaLabel="Add a new bank account"
                    data-tracking="Add Bank Account"
                  />
                  <Span>Add a new bank account</Span>
                </div>
              </div>
              <div className={styles.bottom}>
                {!surchargeOptOut && (
                  <P className={styles.ccFeeDisclaimer}>
                    CC fee/surcharge disclaimer
                  </P>
                )}
                <Span className={styles.bottomNote}>
                  Note: Bank account payments can take up to 7 days to process
                  and clear. Credit/Debit card payments can take up to 2 days to
                  process
                </Span>
                <div className={styles.buttons}>
                  {!isMobileAppView && (
                    <Button
                      className={styles.button}
                      text="Cancel"
                      color="gray"
                      onClick={onClose}
                      type="button"
                      disabled={paymentFetching}
                      data-tracking={
                        isHardLockout
                          ? 'Suspended account payment cancel'
                          : 'Cancel'
                      }
                    />
                  )}
                  <Button
                    className={styles.button}
                    text="Submit"
                    type="submit"
                    loading={paymentFetching}
                    disabled={disableSubmit}
                    data-tracking={
                      isHardLockout
                        ? 'Suspended account payment submit'
                        : 'Submit'
                    }
                  />
                </div>
              </div>
            </ValidatorForm>
            <EditCreditCardDrawer
              drawerProps={{ onClose, hideCloseButton: isMobileAppView }}
              formProps={{ isNew: true, saveForFuture: true }}
            />
            <EditBankAccountDrawer
              drawerProps={{ onClose, hideCloseButton: isMobileAppView }}
              formProps={{ isNew: true, saveForFuture: true }}
            />
          </>
        ) : (
          <div className={styles.submittedDrawer}>
            <Button
              className={styles.backButton}
              text="Back"
              icon={<img src={arrowBackward} alt="backward" />}
              onClick={this.handleClosePaymentSubmittedDialog}
              iconPosition="left"
              link
            />
            <div className={styles.submitSuccessContainer}>
              <div className={styles.submitSuccess}>
                <img src={accountIcons.Finger} alt="finger" />
              </div>
              <Div className={styles.submitSuccess}>
                Your payment has been submitted.
              </Div>
            </div>
          </div>
        )}
        {showInvoiceErrorDialog && (
          <MessageDialog
            open={showInvoiceErrorDialog}
            onClose={() => this.handleCloseInvoiceErrorDialog()}
            message={invoiceErrorMessage}
            isErrorMessage
          />
        )}
        {paymentError && (
          <MessageDialog
            open={paymentError}
            onClose={() => this.handleClosePaymentErrorDialog()}
            message={paymentError}
            isErrorMessage
          />
        )}
        {showPaymentAmountErrorDialog && (
          <MessageDialog
            open={showPaymentAmountErrorDialog}
            onClose={() => this.handleClosePaymentAmountErrorDialog()}
            message={paymentAmountErrorMessage}
            isErrorMessage
          />
        )}
        {showChoosePaymentMethodErrorDialog && (
          <MessageDialog
            open={showChoosePaymentMethodErrorDialog}
            onClose={() => this.handleCloseChoosePaymentMethodErrorDialog()}
            message={choosePaymentMethodErrorMessage}
            isErrorMessage
          />
        )}
        <InvoiceDialog
          open={openInvoiceDialog}
          setStatus={this.hideInvoiceDialog}
          file={invoiceFile}
        />
        <CurrentBalanceDialog
          open={openCurrentBalanceDialog}
          setStatus={this.hideBalanceDialog}
        />
      </Drawer>
    );
  }
}

PayBillDrawer.propTypes = {
  fetchPaymentMethods: PropTypes.func.isRequired,
  showDrawer: PropTypes.func.isRequired,
  makePayment: PropTypes.func.isRequired,
  paymentSubmitted: PropTypes.bool.isRequired,
  paymentFetching: PropTypes.bool.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  paymentMethods: PropTypes.array,
  currentPaymentMethod: PropTypes.object,
  resetMakePayment: PropTypes.func,
  resetPayMyBillError: PropTypes.func,
  bankAccounts: PropTypes.array,
  billingSummary: PropTypes.object,
  resetAddCardSucessful: PropTypes.func,
  isHardLockout: PropTypes.bool.isRequired,
  paymentError: PropTypes.bool,
  creditCardsFetchError: PropTypes.string.isRequired,
  bankAccountsFetchError: PropTypes.string.isRequired,
  billingSummaryFetchError: PropTypes.string.isRequired,
  isMobileAppView: PropTypes.bool.isRequired,
  serviceLocationId: PropTypes.string,
  selectedBillingAccountId: PropTypes.string,
};

PayBillDrawer.defaultProps = {
  currentPaymentMethod: null,
  selectedBillingAccountId: null,
};

const mapStateToProps = createStructuredSelector({
  paymentSubmitted: selectPaymentSubmitted(),
  paymentFetching: selectPaymentFetching(),
  paymentError: selectPaymentError(),
  creditCardsFetchError: selectCreditCardsFetchError(),
  bankAccountsFetchError: selectBankAccountsFetchError(),
  billingSummaryFetchError: selectBillingSummaryFetchError(),
  paymentMethods: selectPaymentMethods(),
  currentPaymentMethod: selectCurrentPaymentMethod(),
  billingSummary: selectBillingSummary(),
  bankAccounts: selectBankAccounts(),
  isHardLockout: selectIsHardLockout(),
  isMobileAppView: selectIsMobileAppView(),
  serviceLocationId: selectSelectedServiceLocationId(),
  selectedBillingAccountId: selectBillingAccountId(),
});

const mapDispatchToProps = {
  fetchPaymentMethods,
  setCurrentPaymentMethod,
  makePayment,
  resetAddCardSucessful,
  resetMakePayment,
  showDrawer: drawerActions.showDrawer,
  resetPayMyBillError,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  PayBillDrawer,
);
